import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { fileUrl } from '../lib/api';
import { usePlayer } from '../context/PlayerContext';
import { Plus, ListMusic, Play } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export const Playlists = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/playlists');
      setPlaylists(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/playlists', { name, description });
      setPlaylists([data, ...playlists]);
      setCreating(false);
      setName(''); setDescription('');
      toast.success('Playlist created');
      navigate(`/playlists/${data.id}`);
    } catch {
      toast.error('Could not create playlist');
    }
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8 flex items-end justify-between flex-wrap gap-4"
      >
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Your crates</div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
            Playlists
          </h1>
        </div>
        <button
          onClick={() => setCreating(!creating)}
          className="btn-pill btn-primary flex items-center gap-2"
          data-testid="new-playlist-btn"
        >
          <Plus size={16} /> New Playlist
        </button>
      </motion.div>

      {creating && (
        <form onSubmit={handleCreate} className="post-card p-6 mb-8 space-y-4">
          <input
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Playlist name"
            className="input-default"
            data-testid="playlist-name"
          />
          <textarea
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="input-default resize-none"
            data-testid="playlist-description"
          />
          <div className="flex gap-2">
            <button type="submit" className="btn-pill btn-primary" data-testid="playlist-create-submit">Create</button>
            <button type="button" onClick={() => setCreating(false)} className="btn-pill btn-outline">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0, 1].map(i => <div key={i} className="post-card h-40 animate-pulse" />)}
        </div>
      ) : playlists.length === 0 ? (
        <div className="post-card p-12 text-center">
          <ListMusic size={36} className="text-[#A1A1AA] mx-auto mb-3" />
          <h3 className="font-heading font-bold text-xl text-white mb-2">No playlists yet</h3>
          <p className="text-[#A1A1AA]">Create your first crate to organize your favorite mixes.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {playlists.map(pl => (
            <Link
              key={pl.id}
              to={`/playlists/${pl.id}`}
              className="post-card p-5 flex items-center gap-4 group"
              data-testid={`playlist-${pl.id}`}
            >
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#FFB000]/30 to-[#FFB000]/10 flex items-center justify-center flex-shrink-0 border border-[#FFB000]/20">
                <ListMusic size={28} className="text-[#FFB000]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-white text-lg truncate group-hover:text-[#FFB000] transition-colors">{pl.name}</h3>
                <p className="text-[#A1A1AA] text-sm truncate">{pl.description || 'No description'}</p>
                <div className="text-xs text-[#52525B] mt-1">{pl.tracks?.length || 0} tracks</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
};

export const PlaylistDetail = () => {
  const { playlistId } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const { playTrack } = usePlayer();

  useEffect(() => {
    api.get(`/playlists/${playlistId}`).then(r => setPlaylist(r.data)).catch(() => {});
  }, [playlistId]);

  if (!playlist) return <Layout><div className="post-card h-60 animate-pulse" /></Layout>;

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Playlist</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter mb-2">
          {playlist.name}
        </h1>
        {playlist.description && <p className="text-[#A1A1AA]">{playlist.description}</p>}
      </div>
      <div className="post-card p-4">
        {playlist.track_details?.length === 0 ? (
          <p className="p-6 text-[#A1A1AA] text-center">No tracks in this playlist yet.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {playlist.track_details?.map((t, i) => (
              <button
                key={t.id}
                onClick={() => playTrack(t, playlist.track_details)}
                className="w-full flex items-center gap-4 p-3 hover:bg-white/5 rounded-xl transition-colors text-left"
                data-testid={`playlist-track-${t.id}`}
              >
                <span className="text-[#A1A1AA] w-6 text-sm">{i + 1}</span>
                <img
                  src={t.cover_path ? fileUrl(t.cover_path) : 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=100'}
                  alt=""
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate font-heading">{t.title}</div>
                  <div className="text-[#A1A1AA] text-xs truncate">{t.author?.name} · {t.genre}</div>
                </div>
                <Play size={16} className="text-[#FFB000]" />
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Playlists;
